import fileDownload from "js-file-download";
import { useEffect, useRef, useState } from "react";
import { FaDownload, FaFileAlt } from "react-icons/fa";
import { MdClose } from "react-icons/md";
import { formatarDataHora } from "../../functions/date";
import { handleError } from "../../services/erros/ErrorHandler";
import { api } from "../../services/apiClient";
import "./style.css";

// Uma ação do chamado no chat: o texto e os arquivos juntos, no mesmo balão, cada arquivo onde foi colado
// (Regra CHA-039 do Raptor, 03/11/2026). `acao.anexos` vem da API ([] quando só tem texto). Imagem aparece no balão; o resto é uma
// linha com o nome e o botão de baixar. O arquivo só sai com login (`/chamadoacao/anexo/:id`), então a
// imagem é baixada como blob, e só quando o balão chega perto da tela.

const ehImagem = (anexo) => {
  const mime = String(anexo?.mime_type || "").toLowerCase();
  return mime.startsWith("image/") && !mime.includes("svg");
};

const formatarTamanho = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1).replace(".", ",")} MB`;
};

// Regra CHA-039 do Raptor: o texto guarda `[[anexo:<id>]]` onde o arquivo foi colado. Vira uma sequência de
// partes (texto e arquivo) na ordem escrita; só vale marcador de arquivo da própria ação, e os que não estão no
// texto voltam em `restantes`, para irem embaixo. O arquivo ocupa a linha dele: a quebra colada nele some.
// `[[arquivo:<i>]]` que sobrou é print da Nova Solicitação que não chegou a subir: não aparece.
const MARCADOR = /\[\[anexo:(\d+)\]\]/g;
export function partesDaAcao(descricao, anexos) {
  const texto = String(descricao || "").replace(/\n?\[\[arquivo:\d+\]\]\n?/g, "");
  const porId = new Map(anexos.map((a) => [Number(a.anexo_id), a]));
  const partes = [];
  const usados = new Set();
  let inicio = 0;
  const empurrarTexto = (trecho) => {
    if (trecho.trim()) partes.push({ texto: trecho });
  };
  for (const m of texto.matchAll(MARCADOR)) {
    const anexo = porId.get(Number(m[1]));
    if (!anexo || usados.has(anexo.anexo_id)) continue;
    empurrarTexto(texto.slice(inicio, m.index).replace(/\n$/, ""));
    partes.push({ anexo });
    usados.add(anexo.anexo_id);
    inicio = m.index + m[0].length;
    if (texto[inicio] === "\n") inicio += 1;
  }
  empurrarTexto(texto.slice(inicio));
  return { partes, restantes: anexos.filter((a) => !usados.has(a.anexo_id)) };
}

async function baixarAnexo(anexo) {
  try {
    const response = await api.get(`/chamadoacao/anexo/${anexo.anexo_id}`, { responseType: "blob" });
    fileDownload(response.data, anexo.nome);
  } catch (error) {
    handleError(error, "Erro ao baixar anexo");
  }
}

function ArquivoAnexo({ anexo }) {
  const tamanho = formatarTamanho(anexo.size_bytes);
  return (
    <div className="anexo_arquivo">
      <FaFileAlt className="anexo_arquivo_icone" />
      <span className="anexo_arquivo_nome" title={anexo.nome}>
        {anexo.nome}
        {tamanho ? <small> · {tamanho}</small> : null}
      </span>
      <button type="button" className="anexo_botao" title="Baixar Anexo" onClick={() => baixarAnexo(anexo)}>
        <FaDownload />
      </button>
    </div>
  );
}

function ImagemAnexo({ anexo, miniatura, onAbrir }) {
  const ref = useRef(null);
  const [url, setUrl] = useState(null);
  const [falhou, setFalhou] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let ativo = true;
    let objectUrl = null;
    const carregar = async () => {
      try {
        const response = await api.get(`/chamadoacao/anexo/${anexo.anexo_id}`, { responseType: "blob" });
        if (!ativo) return;
        objectUrl = URL.createObjectURL(response.data);
        setUrl(objectUrl);
      } catch {
        if (ativo) setFalhou(true);
      }
    };
    const observer = new IntersectionObserver(
      (entradas) => {
        if (entradas.some((e) => e.isIntersecting)) {
          observer.disconnect();
          carregar();
        }
      },
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => {
      ativo = false;
      observer.disconnect();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [anexo.anexo_id]);

  if (falhou) return <ArquivoAnexo anexo={anexo} />;
  return (
    <div ref={ref} className={`anexo_imagem${miniatura ? " miniatura" : ""}`} title={anexo.nome}>
      {url ? (
        <img src={url} alt={anexo.nome} onClick={() => onAbrir({ url, anexo })} onError={() => setFalhou(true)} />
      ) : (
        <div className="anexo_imagem_carregando" />
      )}
    </div>
  );
}

// Texto e arquivos de uma ação, na ordem escrita, com a imagem ampliada no clique. Serve ao balão do chat e à
// Descrição da tela do chamado (que é a ação de abertura).
export function ConteudoDaAcao({ descricao, anexos: anexosProp }) {
  const anexos = Array.isArray(anexosProp) ? anexosProp : [];
  const { partes, restantes } = partesDaAcao(descricao, anexos);
  const imagens = restantes.filter(ehImagem);
  const outros = restantes.filter((a) => !ehImagem(a));
  const [ampliada, setAmpliada] = useState(null);

  useEffect(() => {
    if (!ampliada) return undefined;
    const aoTeclar = (e) => e.key === "Escape" && setAmpliada(null);
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [ampliada]);

  return (
    <>
      {partes.map((p, i) =>
        p.anexo ? (
          ehImagem(p.anexo) ? (
            <div key={`a${p.anexo.anexo_id}`} className="anexo_no_texto">
              <ImagemAnexo anexo={p.anexo} onAbrir={setAmpliada} />
            </div>
          ) : (
            <ArquivoAnexo key={`a${p.anexo.anexo_id}`} anexo={p.anexo} />
          )
        ) : (
          <div key={`t${i}`} className="balao_texto">
            {p.texto}
          </div>
        )
      )}
      {imagens.length ? (
        <div className="anexos_imagens">
          {imagens.map((a) => (
            <ImagemAnexo key={a.anexo_id} anexo={a} miniatura={imagens.length > 1} onAbrir={setAmpliada} />
          ))}
        </div>
      ) : null}
      {outros.map((a) => (
        <ArquivoAnexo key={a.anexo_id} anexo={a} />
      ))}

      {ampliada ? (
        <div className="imagem_ampliada" onClick={() => setAmpliada(null)}>
          <div className="imagem_ampliada_botoes" onClick={(e) => e.stopPropagation()}>
            <button type="button" title="Baixar Anexo" onClick={() => baixarAnexo(ampliada.anexo)}>
              <FaDownload />
            </button>
            <button type="button" title="Fechar" onClick={() => setAmpliada(null)}>
              <MdClose />
            </button>
          </div>
          <img src={ampliada.url} alt={ampliada.anexo.nome} onClick={(e) => e.stopPropagation()} />
        </div>
      ) : null}
    </>
  );
}

export default function ChamadoCardChat({ acao }) {
  const who_action = acao.acao_de === 0 ? "cli_action" : "cmx_action"; // subs por enum
  return (
    <div className={`action ${who_action}`}>
      <div className="balao">
        <ConteudoDaAcao descricao={acao.descricao} anexos={acao.anexos} />
        <span className="balao_rodape">
          {acao.criado_por} {formatarDataHora(acao.criado_em)}
        </span>
      </div>
    </div>
  );
}
